import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import { SxProps, Theme, useTheme } from "@mui/material/styles";
import { FC, Suspense, memo, useEffect, useMemo } from "react";

import { useDialog } from "@/hooks";
import { preloadDeviceProof } from "@/lib/fingerprint";
import { isDeviceProofWasted } from "@/lib/geoAccess";
import { store } from "@/store";

import {
  BARE_DIALOGS,
  DIALOG_COMPONENTS,
  DIALOG_MAX_WIDTH,
  DIALOG_PAPER_WIDTH,
  NON_FULLSCREEN_DIALOGS,
  preloadAuthDialog,
} from "./data";
import { DIALOG_BACKDROP_SX, dialogPaperSurface } from "./surface";
import { DialogType } from "./type";

const BARE_PAPER = {
  minWidth: "unset",
  width: "auto",
  margin: 0,
  backgroundColor: "transparent",
  border: "none",
  boxShadow: "none",
  // Lets a celebration fly past the content's edge.
  overflow: "visible",
} as const;

const BARE_PAPER_SX: SxProps<Theme> = (theme) => ({
  "& .MuiDialog-paper": { ...BARE_PAPER, ...theme.applyStyles("dark", BARE_PAPER) },
});

// While a dialog's chunk arrives (only on its first open).
const DIALOG_FALLBACK = (
  <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
    <CircularProgress size={28} />
  </Box>
);

const preloadSignIn = () => {
  preloadAuthDialog();
  if (!store.getState().auth.isLoggedIn && !isDeviceProofWasted()) preloadDeviceProof();
};

const DialogsComponent: FC = () => {
  const theme = useTheme();
  const { activeDialog, closeDialog } = useDialog();

  // Once the page has settled (after the hero image), so sign-in opens without a wait.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (window.requestIdleCallback) window.requestIdleCallback(preloadSignIn);
      else preloadSignIn();
    }, 4000);
    return () => window.clearTimeout(timer);
  }, []);

  const DialogComponent = useMemo(
    () => (activeDialog ? DIALOG_COMPONENTS[activeDialog as DialogType] : null),
    [activeDialog],
  );

  const keepNormalSizeOnMobile = !!(
    activeDialog && NON_FULLSCREEN_DIALOGS.has(activeDialog as DialogType)
  );

  const paperWidth = activeDialog ? DIALOG_PAPER_WIDTH[activeDialog as DialogType] : undefined;
  const bare = !!(activeDialog && BARE_DIALOGS.has(activeDialog as DialogType));

  const paperStyles = useMemo(
    () => ({
      ...dialogPaperSurface(theme),
      ...(paperWidth && { maxWidth: paperWidth }),
      ...(keepNormalSizeOnMobile && {
        // MUI's own max-height (the viewport less 64px) stays, and with it
        // the paper's scrolling: without it the register form ran past both
        // edges of a small phone and its close button sat off-screen.
        [theme.breakpoints.down(426)]: {
          maxWidth: "unset",
          width: "unset",
          margin: theme.spacing(4),
          borderRadius: theme.shape.borderRadius,
        },
      }),
    }),
    [theme, keepNormalSizeOnMobile, paperWidth],
  );

  const hasWidthOverride = !!(activeDialog && DIALOG_MAX_WIDTH[activeDialog as DialogType]);
  const maxWidth = (activeDialog && DIALOG_MAX_WIDTH[activeDialog as DialogType]) || "sm";
  const isWideDialog = hasWidthOverride;

  return (
    <Dialog
      open={!!activeDialog}
      onClose={closeDialog}
      PaperProps={{ sx: paperStyles }}
      // On the Dialog, not the paper: the theme's dark paper colour is set from here too.
      sx={bare ? BARE_PAPER_SX : undefined}
      slotProps={{ backdrop: { sx: DIALOG_BACKDROP_SX } }}
      maxWidth={maxWidth}
      fullWidth={isWideDialog}
      aria-labelledby="dialog-title"
    >
      {DialogComponent && (
        <Suspense fallback={DIALOG_FALLBACK}>
          <DialogComponent />
        </Suspense>
      )}
    </Dialog>
  );
};

export default memo(DialogsComponent);
